import { useContext, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Tooltip from '@mui/material/Tooltip';
import { UMInputNumber } from '@urbanmetrix/um-kit';
import { GridContext } from 'contexts/GridContext';
import { ValidationMinMaxContext } from 'contexts/ValidationMinMaxContext';
import { ABSOLUTE_FLAT_TERRAIN_HEIGHT_MAX, ABSOLUTE_FLAT_TERRAIN_HEIGHT_MIN } from 'constants/grid';
import { getInputNumberRangeErrorMessage, isInputNumberInRange } from 'utils/inputNumberErrorMessage';
import CustomTooltip from 'components/tooltip/CustomTooltip';
import OtherInfoForSite from './OtherInfoForSite';
import { ArrowRightIcon, CompassIcon } from 'components/icons';

interface GridSettingsFormProps {
  onOpenGeodataSettings?: () => void;
  readOnly?: boolean;
  showGeodataSettingsLink?: boolean;
  disableGeodataSettingsLink?: boolean;
  onBillingLinkClick?: () => void;
  onGridResolutionChange?: () => void;
}

const GridSettingsForm = ({
  onOpenGeodataSettings,
  readOnly = false,
  showGeodataSettingsLink = true,
  disableGeodataSettingsLink = false,
  onBillingLinkClick,
  onGridResolutionChange,
}: GridSettingsFormProps) => {
  const { gridParams, setGridParams } = useContext(GridContext);
  const { isValidGridMinMaxInputs, setIsValidGridMinMaxInputs } = useContext(ValidationMinMaxContext);
  const [isGridAngleEnabled, setIsGridAngleEnabled] = useState(Boolean(gridParams.gridAngle));
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(
    Boolean(gridParams.customGridElevationMode || gridParams.gridAngle || isGridAngleEnabled)
  );
  const isFlatTerrainHeightValid = isInputNumberInRange(
    gridParams.flatTerrainHeight,
    ABSOLUTE_FLAT_TERRAIN_HEIGHT_MIN,
    ABSOLUTE_FLAT_TERRAIN_HEIGHT_MAX
  );
  const isGridAngleValid = isInputNumberInRange(gridParams.gridAngle, 0, 359.99);
  const isCustomGridElevationValid = isInputNumberInRange(gridParams.customGridElevation, 0, 10000);

  const handleInputsChange = (name: string | undefined, value: string | number, valid?: boolean) => {
    if (name === 'gridResolution') onGridResolutionChange?.();
    setGridParams({ ...gridParams, [name as string]: value });
    setIsValidGridMinMaxInputs({
      ...isValidGridMinMaxInputs,
      [name as string]: valid,
    });
  };

  const handleFlatCloseTerrainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setGridParams({
      flatTerrainMode: checked ? (gridParams.flatTerrainMode ?? 'Average') : null,
      flatTerrainHeight:
        checked && gridParams.flatTerrainMode === 'Absolute'
          ? Math.max(gridParams.flatTerrainHeight || ABSOLUTE_FLAT_TERRAIN_HEIGHT_MIN, ABSOLUTE_FLAT_TERRAIN_HEIGHT_MIN)
          : checked
            ? gridParams.flatTerrainHeight || 1
            : 0,
    });
  };

  const handleFlatCloseTerrainModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextMode = event.target.value;
    setGridParams({
      flatTerrainMode: nextMode,
      ...(nextMode === 'Absolute'
        ? {
            flatTerrainHeight: Math.max(
              gridParams.flatTerrainHeight || ABSOLUTE_FLAT_TERRAIN_HEIGHT_MIN,
              ABSOLUTE_FLAT_TERRAIN_HEIGHT_MIN
            ),
          }
        : {}),
    });
  };

  const handleCustomGridElevationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGridParams({
      customGridElevationMode: event.target.checked ? (gridParams.customGridElevationMode ?? 'Average') : null,
    });
  };

  const handleCustomGridElevationModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGridParams({ customGridElevationMode: event.target.value });
  };

  const handleCheckboxChange =
    (name: 'isFlatTop' | 'gridAngleEnabled') => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (name === 'gridAngleEnabled') {
        setIsGridAngleEnabled(event.target.checked);
        setGridParams({
          gridAngle: event.target.checked ? gridParams.gridAngle || 0 : 0,
        });
        return;
      }

      setGridParams({
        isFlatTop: event.target.checked,
      });
    };

  const hasFlatCloseTerrain = gridParams.flatTerrainMode != null;
  const hasCustomGridElevation = gridParams.customGridElevationMode != null;
  const hasCustomGridAngle = isGridAngleEnabled;
  const hasAdvancedSettingsError =
    (hasCustomGridAngle && !isGridAngleValid) ||
    (gridParams.customGridElevationMode === 'Absolute' && !isCustomGridElevationValid);

  const isGridResolutionValid = isInputNumberInRange(gridParams.gridResolution, 1, 20);

  return (
    <div className="grid-settings-panel">
      <div className="grid-settings-panel__section grid-settings-panel__section--stack">
        <div className="analysis-area-volume-field__title text-sm font-semibold">Define Grid Settings</div>
        <UMInputNumber
          label={
            <span className="grid-inline-label-with-info">
              <span>Grid Resolution [m]</span>
              <CustomTooltip
                title="In most cases it is recommended to set the grid resolution to match one floor-step (e.g. 3m). For very small sites you can increase the resolution up to 1m."
                arrow
              />
            </span>
          }
          value={gridParams.gridResolution}
          width={84}
          maxErrorMessageWidth={276}
          name="gridResolution"
          onChange={handleInputsChange}
          defaultValue={1}
          min={1}
          max={20}
          errorMessage={getInputNumberRangeErrorMessage(1, 20)}
          valid={isGridResolutionValid}
          disabled={readOnly}
        />
      </div>

      <div className="grid-settings-panel__section grid-settings-panel__checkboxes grid-settings-panel__checkboxes--flat-terrain">
        <FormControlLabel
          className="checkbox-row"
          control={
            <Checkbox checked={hasFlatCloseTerrain} onChange={handleFlatCloseTerrainChange} disabled={readOnly} />
          }
          label={
            <span className="grid-inline-label-with-info">
              <span>Flat Grid Terrain</span>
              <CustomTooltip title="Flattens the terrain only under the Analysis Grid." />
            </span>
          }
        />
        {hasFlatCloseTerrain && (
          <>
            <RadioGroup
              row
              value={gridParams.flatTerrainMode ?? 'Average'}
              onChange={handleFlatCloseTerrainModeChange}
              className="grid-settings-panel__radios"
            >
              <FormControlLabel value="Average" control={<Radio disabled={readOnly} />} label="Average" />
              <FormControlLabel value="Lowest" control={<Radio disabled={readOnly} />} label="Lowest" />
              <FormControlLabel value="Highest" control={<Radio disabled={readOnly} />} label="Highest" />
              <FormControlLabel value="Absolute" control={<Radio disabled={readOnly} />} label="Absolute" />
            </RadioGroup>
            {gridParams.flatTerrainMode === 'Absolute' && (
              <div className="grid-settings-panel__absolute-height">
                <div>The elevation must be given in meters above mean sea level.</div>
                <div className="grid-settings-panel__absolute-height-input">
                  <UMInputNumber
                    name="flatTerrainHeight"
                    value={gridParams.flatTerrainHeight}
                    width={84}
                    maxErrorMessageWidth={248}
                    onChange={handleInputsChange}
                    defaultValue={ABSOLUTE_FLAT_TERRAIN_HEIGHT_MIN}
                    min={ABSOLUTE_FLAT_TERRAIN_HEIGHT_MIN}
                    max={ABSOLUTE_FLAT_TERRAIN_HEIGHT_MAX}
                    errorMessage={getInputNumberRangeErrorMessage(
                      ABSOLUTE_FLAT_TERRAIN_HEIGHT_MIN,
                      ABSOLUTE_FLAT_TERRAIN_HEIGHT_MAX
                    )}
                    valid={isFlatTerrainHeightValid}
                    disabled={readOnly}
                  />
                  <span>[m]</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="grid-settings-panel__section grid-settings-panel__checkboxes">
        <FormControlLabel
          className="checkbox-row"
          control={
            <Checkbox
              checked={Boolean(gridParams.isFlatTop)}
              onChange={handleCheckboxChange('isFlatTop')}
              disabled={readOnly}
            />
          }
          label={
            <span className="grid-inline-label-with-info">
              <span>Flat Grid Top</span>
              <CustomTooltip title="If selected the top of the grid will be flattened at the height of the lowest point of the top." />
            </span>
          }
        />
      </div>

      <div className="grid-settings-panel__section">
        <button
          type="button"
          className={`grid-settings-panel__advanced-toggle${hasAdvancedSettingsError ? ' grid-settings-panel__advanced-toggle--error' : ''}`}
          onClick={() => setShowAdvancedSettings((prev) => !prev)}
        >
          <span className="grid-settings-panel__advanced-toggle-icon">{showAdvancedSettings ? '▾' : '▸'}</span>
          <span>Advanced settings</span>
        </button>

        {showAdvancedSettings && (
          <div className="grid-settings-panel__advanced">
            <div className="grid-settings-panel__advanced-item">
              <FormControlLabel
                className="checkbox-row"
                control={
                  <Checkbox
                    checked={hasCustomGridAngle}
                    onChange={handleCheckboxChange('gridAngleEnabled')}
                    disabled={readOnly}
                  />
                }
                label={
                  <span className="grid-inline-label-with-info">
                    <span>Grid Angle [°]</span>
                    <CustomTooltip title="By default the grid is oriented along cardinal directions, set an angle if you need a custom orientation." />
                  </span>
                }
              />
              {hasCustomGridAngle && (
                <div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <UMInputNumber
                      label=""
                      value={gridParams.gridAngle}
                      width={84}
                      hideErrorMessage
                      name="gridAngle"
                      onChange={handleInputsChange}
                      defaultValue={0}
                      min={0}
                      max={359.99}
                      errorMessage={getInputNumberRangeErrorMessage(0, 359.99)}
                      valid={isGridAngleValid}
                      disabled={readOnly}
                    />
                    <Tooltip title="Degrees °, CCW from E . Click on an analysis area side on 2D map to copy its angle.">
                      <span style={{ display: 'inline-flex', alignItems: 'center', color: '#6b7280' }}>
                        <CompassIcon />
                      </span>
                    </Tooltip>
                  </div>
                  {!isGridAngleValid && (
                    <div
                      style={{
                        color: '#d32f2f',
                        fontSize: '0.7rem',
                        lineHeight: 1.25,
                        maxWidth: 260,
                        marginTop: 2,
                      }}
                    >
                      {getInputNumberRangeErrorMessage(0, 359.99)}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid-settings-panel__advanced-item">
              <FormControlLabel
                className="checkbox-row"
                control={
                  <Checkbox
                    checked={hasCustomGridElevation}
                    onChange={handleCustomGridElevationChange}
                    disabled={readOnly}
                  />
                }
                label={
                  <span className="grid-inline-label-with-info">
                    <span>Grid Elevation [m]</span>
                    <CustomTooltip
                      title={
                        <>
                          If not selected the grid elevation is derived from the 3D shape of the terrain underneath the
                          grid. You can retrieve the height with a right click on{' '}
                          <a
                            href="https://map.geo.admin.ch/"
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            style={{ textDecoration: 'underline', cursor: 'pointer' }}
                          >
                            map.geo.admin.ch
                          </a>
                          .
                        </>
                      }
                    />
                  </span>
                }
              />
              {hasCustomGridElevation && (
                <>
                  <RadioGroup
                    row
                    value={gridParams.customGridElevationMode ?? 'Average'}
                    onChange={handleCustomGridElevationModeChange}
                    className="grid-settings-panel__radios"
                  >
                    <FormControlLabel value="Average" control={<Radio disabled={readOnly} />} label="Average" />
                    <FormControlLabel value="Lowest" control={<Radio disabled={readOnly} />} label="Lowest" />
                    <FormControlLabel value="Highest" control={<Radio disabled={readOnly} />} label="Highest" />
                    <FormControlLabel value="Absolute" control={<Radio disabled={readOnly} />} label="Absolute" />
                  </RadioGroup>
                  {gridParams.customGridElevationMode === 'Absolute' && (
                    <div className="grid-settings-panel__absolute-height">
                      <div>The elevation must be given in meters above mean sea level.</div>
                      <div className="grid-settings-panel__absolute-height-input">
                        <UMInputNumber
                          name="customGridElevation"
                          value={gridParams.customGridElevation}
                          width={84}
                          maxErrorMessageWidth={260}
                          onChange={handleInputsChange}
                          defaultValue={1}
                          min={0}
                          max={10000}
                          errorMessage={getInputNumberRangeErrorMessage(0, 10000)}
                          valid={isCustomGridElevationValid}
                          disabled={readOnly}
                        />
                        <span>[m]</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="grid-settings-panel__section">
        <OtherInfoForSite
          showHeightInput={false}
          showVolumeSummary={false}
          showSensorPointsSummary
          readOnly={readOnly}
          onBillingLinkClick={onBillingLinkClick}
        />
      </div>

      {!readOnly && showGeodataSettingsLink && (
        <div className="grid-settings-panel__section">
          <button
            type="button"
            className="grid-settings-panel__link"
            onClick={onOpenGeodataSettings}
            disabled={disableGeodataSettingsLink}
          >
            <span>Geodata Settings</span>
            <ArrowRightIcon className="grid-settings-panel__link-icon" />
          </button>
        </div>
      )}
    </div>
  );
};

export default GridSettingsForm;
